/**
 * The pure half of About: turning bridge snapshots into the lines it shows.
 *
 * Kept out of the component for the same reason `hostStatus.ts` is — the
 * distinctions here are the ones the dialog exists to preserve, and a rule that
 * only exists inside JSX is a rule no test can hold to.
 *
 * Every function takes "could not look" as a real input, because About is the
 * screen someone opens *because* something is wrong: the desktop may be too old
 * to report plugins, or the provisioner may refuse while Revit is open. Each of those has to read differently from
 * "looked, found nothing" (Corrections #23).
 */
import type { DetectSnapshot } from "../bimgo";

/**
 * The installed plugin, one line per Revit year.
 *
 * Years with nothing of ours are skipped rather than listed as absent: a machine
 * with Revit 2021–2026 installed would otherwise produce six lines of "not
 * installed" to hide the one line that matters.
 *
 * The three states that *are* reported are separate on purpose, because they
 * send a reader to three different places:
 *
 *   - **A developer build** — the manifest points outside the managed tree
 *     (`install-dev-addin.ps1`). Reporting this as "not installed" sends a
 *     developer hunting for a broken install that is working exactly as they
 *     set it up.
 *   - **Manifest present, files missing** — Revit will try to load an assembly
 *     that is not there. That is a real broken state and it names itself.
 *   - **Installed** — with the version, or with the fact that the build declares
 *     none. "Installed" and "which build" are separate facts; a build with no
 *     version resource is installed all the same.
 */
export function describeInstalledPlugins(
  snapshot: DetectSnapshot | null | undefined,
): string[] {
  if (!snapshot) return [];

  const lines: string[] = [];
  for (const host of snapshot.hosts) {
    for (const year of host.versions) {
      if (!year.addinPresent && !year.treePresent) continue;

      const label = `${host.displayName} ${year.displayName || year.version}`;
      if (year.activeExternal) {
        lines.push(`${label} — developer build (manifest points outside the managed tree)`);
      } else if (!year.treePresent) {
        lines.push(`${label} — manifest present, files missing`);
      } else {
        lines.push(`${label} — ${year.installedPluginVersion ?? "version not reported"}`);
      }
    }
  }
  return lines;
}
